import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar2 from '../components/Navbar2';
import { createProject } from '../services/database';


const CHAR_LIMITS = {
  title: { max: 100 },
  description: { max: 1000 },
  userProfile: { max: 500 }
};

const NewProject = () => {
  
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    userProfile: ''
  });

  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    const validateForm = () => {
      return Object.entries(formData).every(([field, value]) => 
        value.length > 0 && value.length <= CHAR_LIMITS[field].max
      );
    };
    
    setIsValid(validateForm());
  }, [formData]);

  const getCharacterCount = (field) => {
    const count = formData[field].length;
    const { max } = CHAR_LIMITS[field];
    
    if (count > max) {
      return `${count - max} characters over limit`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) return;
    
    setIsLoading(true);
    setError(null);
  
    try {
      const projectData = {
        title: formData.title,
        description: formData.description,
        userProfile: formData.userProfile,
        styleChoices: {
          device: 'Desktop',
          theme: 'light',
          palette: 'Classic Light',
          font: 'Native UI',
        },
      };
  
      const response = await createProject(projectData);
      const { projectId } = response;
  
      // Navigate to StyleChoices and pass the projectId
      navigate('/style-choices', { state: { projectId } });
      
    } catch (error) {
      console.error('Error creating project:', error);
      setError('Failed to create project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans antialiased">
      <Navbar2 
        title="Create New Project" 
        backPath="/projects" 
      />
      
      <div className="px-4 md:px-8 lg:px-16 py-12 max-w-4xl mx-auto">
        <div className="text-left mb-8">
          <p className="text-lg text-gray-700">
            Please provide detailed information about your project. 
            The more specific you are, the better our AI can understand 
            your needs and generate relevant designs for you.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-lg font-semibold text-gray-800 mb-2">
              Project Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter a clear, descriptive title for your project"
              className="w-full p-3 border rounded-md"
            />
            {getCharacterCount('title') && (
              <p className="mt-1 text-sm text-red-500">
                {getCharacterCount('title')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-800 mb-2">
              Project Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Describe what you want to build. What problems does it solve? What features are most important? What's the main purpose of your application?"
              className="w-full p-3 border rounded-md"
            />
            {getCharacterCount('description') && (
              <p className="mt-1 text-sm text-red-500">
                {getCharacterCount('description')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-800 mb-2">
              User Profile
            </label>
            <textarea
              name="userProfile"
              value={formData.userProfile}
              onChange={handleChange}
              rows={4}
              placeholder="Describe your target users. What are their roles, ages, and technical expertise? What motivates them? What are their goals and pain points?"
              className="w-full p-3 border rounded-md"
            />
            {getCharacterCount('userProfile') && (
              <p className="mt-1 text-sm text-red-500">
                {getCharacterCount('userProfile')}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`px-6 py-3 rounded-md text-white relative
              ${isValid && !isLoading
                ? 'bg-black hover:bg-gray-800' 
                : 'bg-gray-400 cursor-not-allowed'
              } transition-colors duration-200`}
          >
            {isLoading ? (
              <>
                <span className="opacity-0">Create Project</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              'Create Project'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewProject;